/**
 * Production-Ready Pending Invitations Check Manager
 *
 * Features:
 * - Periodic check: every 5 minutes to check for pending invitations
 * - Tab coordination: BroadcastChannel to sync across tabs
 * - Heartbeat backup: checks every 2 minutes if setTimeout fails
 * - Visibility handling: immediate check when tab becomes visible (if interval elapsed)
 * - Single-flight pattern: only one check at a time
 * - Login trigger: checks immediately on login
 */

import { listMyInvitations } from '@/services/api';

// Constants
const CHECK_CHANNEL = 'pending-invitations-check';
const CHECK_LOCK_KEY = 'pending-invitations-check-lock';
const LAST_CHECK_KEY = 'pending-invitations-last-check';
// Ten seconds max lock duration
const LOCK_TIMEOUT = 10000;
// Five minutes - check more frequently than outdated agents since invitations are more urgent
const CHECK_INTERVAL = 5 * 60 * 1000;
// Two minutes - backup check mechanism
const HEARTBEAT_INTERVAL = 2 * 60 * 1000;

interface CheckMessage {
  type: 'check-started' | 'check-completed' | 'check-failed';
  timestamp: number;
  pendingCount?: number;
}

export type PendingInvitationsCallback = (count: number) => void;

class PendingInvitationsCheckManager {
  private checkTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private checkPromise: Promise<void> | null = null;
  private callback: PendingInvitationsCallback | null = null;
  private channel: BroadcastChannel | null = null;
  private isPaused = false;
  private lastCheckAttempt = 0;
  private isInitialized = false;

  constructor() {
    // Initialize BroadcastChannel if supported
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel(CHECK_CHANNEL);
        this.channel.onmessage = (event: MessageEvent<CheckMessage>) => {
          this.handleChannelMessage(event.data);
        };
      } catch (error) {
        console.warn('[PendingInvitationsCheck] BroadcastChannel not available:', error);
      }
    }

    // Listen for visibility changes
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        this.handleVisibilityChange();
      });
    }
  }

  /**
   * Handle messages from other tabs
   */
  private handleChannelMessage(message: CheckMessage): void {
    if (message.type === 'check-completed' && message.pendingCount !== undefined) {
      // Update our local state with the count from another tab
      if (this.callback) {
        this.callback(message.pendingCount);
      }
      // Update last check time
      this.updateLastCheckTime();
    }
  }

  /**
   * Handle visibility changes - check when tab becomes visible if needed
   */
  private handleVisibilityChange(): void {
    if (typeof document === 'undefined') return;

    const isHidden = document.hidden;

    if (isHidden && !this.isPaused) {
      this.isPaused = true;
    } else if (!isHidden && this.isPaused) {
      this.isPaused = false;

      // Check if we need to run a check (if interval has elapsed)
      if (this.shouldCheck()) {
        console.log('[PendingInvitationsCheck] Tab visible and interval elapsed, checking now');
        void this.executeCheck();
      }
    }
  }

  /**
   * Check if we should run a check based on last check time
   */
  private shouldCheck(): boolean {
    if (!this.isInitialized) return false;

    const lastCheck = this.getLastCheckTime();
    if (!lastCheck) return true;

    return Date.now() - lastCheck >= CHECK_INTERVAL;
  }

  /**
   * Get last check time from localStorage
   */
  private getLastCheckTime(): number | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const stored = localStorage.getItem(LAST_CHECK_KEY);
      return stored ? parseInt(stored, 10) : null;
    } catch {
      return null;
    }
  }

  /**
   * Update last check time in localStorage
   */
  private updateLastCheckTime(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
    } catch (error) {
      console.warn('[PendingInvitationsCheck] Failed to update last check time:', error);
    }
  }

  /**
   * Heartbeat mechanism - runs every 2 minutes as backup to setTimeout
   */
  private heartbeat(): void {
    if (!this.isInitialized || this.isPaused) {
      return;
    }

    if (this.shouldCheck()) {
      console.log('[PendingInvitationsCheck] Heartbeat detected need for check');
      void this.executeCheck();
    }
  }

  /**
   * Start heartbeat timer
   */
  private startHeartbeat(): void {
    if (this.heartbeatTimer) return;

    this.heartbeatTimer = setInterval(() => {
      this.heartbeat();
    }, HEARTBEAT_INTERVAL);
  }

  /**
   * Stop heartbeat timer
   */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Try to acquire a lock for check (localStorage-based for cross-tab)
   */
  private tryAcquireLock(): boolean {
    if (typeof localStorage === 'undefined') return true;

    try {
      const lockData = localStorage.getItem(CHECK_LOCK_KEY);
      if (lockData) {
        const lock = JSON.parse(lockData) as { timestamp: number };
        const lockAge = Date.now() - lock.timestamp;

        if (lockAge < LOCK_TIMEOUT) {
          // Lock held by another tab
          return false;
        }
      }

      // Acquire lock
      localStorage.setItem(CHECK_LOCK_KEY, JSON.stringify({ timestamp: Date.now() }));
      return true;
    } catch (error) {
      console.warn('[PendingInvitationsCheck] Lock acquisition failed:', error);
      // Proceed if localStorage unavailable
      return true;
    }
  }

  /**
   * Release the check lock
   */
  private releaseLock(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.removeItem(CHECK_LOCK_KEY);
    } catch (error) {
      console.warn('[PendingInvitationsCheck] Lock release failed:', error);
    }
  }

  /**
   * Broadcast message to other tabs
   */
  private broadcast(type: CheckMessage['type'], pendingCount?: number): void {
    if (this.channel) {
      try {
        // eslint-disable-next-line unicorn/require-post-message-target-origin -- BroadcastChannel doesn't use targetOrigin
        this.channel.postMessage({ type, timestamp: Date.now(), pendingCount } as CheckMessage);
      } catch (error) {
        console.warn('[PendingInvitationsCheck] Broadcast failed:', error);
      }
    }
  }

  /**
   * Execute check with single-flight pattern
   */
  async executeCheck(): Promise<void> {
    // Rate limiting: prevent check spam (max once per 10 seconds)
    const now = Date.now();
    if (now - this.lastCheckAttempt < 10000) {
      console.log('[PendingInvitationsCheck] Rate limited, skipping check');
      return;
    }
    this.lastCheckAttempt = now;

    // Single-flight: if already checking, return existing promise
    if (this.checkPromise) {
      console.log('[PendingInvitationsCheck] Check already in progress, waiting...');
      return this.checkPromise;
    }

    // Try to acquire lock
    if (!this.tryAcquireLock()) {
      console.log('[PendingInvitationsCheck] Another tab is checking, waiting...');
      return;
    }

    // Broadcast that we're starting check
    this.broadcast('check-started');

    // Create and store the promise
    this.checkPromise = (async () => {
      try {
        console.log('[PendingInvitationsCheck] Starting pending invitations check...');

        // Fetch pending invitations only
        const { data: invitations } = await listMyInvitations({
          query: { status_filter: 'pending' },
          throwOnError: true,
        });

        const pendingCount = invitations.length;

        console.log(`[PendingInvitationsCheck] Check complete: ${pendingCount} pending invitation(s) found`);

        // Update callback
        if (this.callback) {
          this.callback(pendingCount);
        }

        // Update last check time
        this.updateLastCheckTime();

        // Broadcast completion to other tabs
        this.broadcast('check-completed', pendingCount);

        // Schedule next check
        this.scheduleNextCheck();
      } catch (error) {
        console.error('[PendingInvitationsCheck] Check failed:', error);
        this.broadcast('check-failed');

        // Still schedule next check even on failure
        this.scheduleNextCheck();
      } finally {
        this.releaseLock();
        this.checkPromise = null;
      }
    })();

    return this.checkPromise;
  }

  /**
   * Schedule the next check
   */
  private scheduleNextCheck(): void {
    // Clear any existing timer
    if (this.checkTimer) {
      clearTimeout(this.checkTimer);
      this.checkTimer = null;
    }

    // Don't schedule if paused
    if (this.isPaused) {
      console.log('[PendingInvitationsCheck] Tab hidden, will check on visibility');
      return;
    }

    console.log(`[PendingInvitationsCheck] Next check scheduled in ${Math.floor(CHECK_INTERVAL / 60000)} minutes`);

    this.checkTimer = setTimeout(() => {
      console.log('[PendingInvitationsCheck] Scheduled check triggered');
      void this.executeCheck();
    }, CHECK_INTERVAL);
  }

  /**
   * Initialize the manager and start periodic checks
   * Call this after login
   */
  start(callback: PendingInvitationsCallback): void {
    console.log('[PendingInvitationsCheck] Starting pending invitations check manager');

    this.callback = callback;
    this.isInitialized = true;

    // Start heartbeat
    this.startHeartbeat();

    // Execute immediate check
    void this.executeCheck();
  }

  /**
   * Stop the manager
   * Call this on logout
   */
  stop(): void {
    console.log('[PendingInvitationsCheck] Stopping pending invitations check manager');

    this.isInitialized = false;
    this.callback = null;

    // Clear timers
    if (this.checkTimer) {
      clearTimeout(this.checkTimer);
      this.checkTimer = null;
    }
    this.stopHeartbeat();

    // Release lock if held
    this.releaseLock();
  }

  /**
   * Force an immediate check
   * Useful for manual triggers
   */
  forceCheck(): void {
    if (!this.isInitialized) {
      console.warn('[PendingInvitationsCheck] Manager not initialized');
      return;
    }

    // Reset rate limit to allow immediate check
    this.lastCheckAttempt = 0;
    void this.executeCheck();
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stop();
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
  }
}

// Export singleton instance
export const pendingInvitationsCheckManager = new PendingInvitationsCheckManager();
